import i18n from "@/i18n";
import globalMethods from "@/globalMethods";
import {URLS} from "@/constants";
import {notify} from "@/plugins/notification";


export default {
    namespaced: true,
    state: {
        lastSaleId: null,
        transactions: [],
        nonSaleTransactions: [],
        salesPagination: {
            page: 1,
            pageSize: 50,
            hasNext: false,
            totalCount: 0,
            isLoading: false,
        },
    },
    mutations: {
        setLastSaleId(state, lastSaleId) {
            state.lastSaleId = lastSaleId;
        },
        setTransactions(state, newTransactionsData) {
            state.transactions = newTransactionsData;
        },
        setNonSaleTransactions(state, data) {
            state.nonSaleTransactions = data;
        },
        setSalesPaginated(state, { data, hasNext, totalCount, append }) {
            if (append) {
                const existingIds = new Set(state.transactions.filter(t => t.verbose_id).map(t => t.id));
                const newSales = data.filter(s => !existingIds.has(s.id));
                const allSales = [...state.transactions.filter(t => t.verbose_id), ...newSales];
                const merged = [...allSales, ...state.nonSaleTransactions];
                merged.sort((a, b) => new Date(b.date_of_created) - new Date(a.date_of_created));
                state.transactions = merged;
            } else {
                const merged = [...data, ...state.nonSaleTransactions];
                merged.sort((a, b) => new Date(b.date_of_created) - new Date(a.date_of_created));
                state.transactions = merged;
            }
            state.salesPagination.hasNext = hasNext;
            state.salesPagination.totalCount = totalCount;
        },
        setSalesPaginationLoading(state, isLoading) {
            state.salesPagination.isLoading = isLoading;
        },
        setSalesPaginationPage(state, page) {
            state.salesPagination.page = page;
        },
        resetSalesPagination(state) {
            state.salesPagination.page = 1;
            state.salesPagination.hasNext = false;
            state.salesPagination.totalCount = 0;
        },
        addTransaction(state, newTransactionData) {
            const exists = state.transactions.some(t => t.id === newTransactionData.id);
            if (!exists) {
                state.transactions.unshift(newTransactionData);
            }
        },
        deleteTransaction(state, transactionData) {
            const transaction = state.transactions.find(transaction => transaction.id === transactionData.id);
            if (transaction) transaction.is_active = false;
        },
    },
    actions: {
        // Добавить транзакцию из SSE (real-time обновление)
        addTransactionFromSSE({ commit, state, rootState }, sale) {
            const currentShopId = rootState.cashRegister.cashRegisterData?.id
            // Добавлять только транзакции текущей кассы и только если её ещё нет в списке
            if (sale.shop_id === currentShopId) {
                const exists = state.transactions.some(t => t.id === sale.id)
                if (!exists) {
                    commit('addTransaction', sale)
                }
            }
        },
        // Удалить транзакцию из SSE (real-time обновление)
        removeTransactionFromSSE({ state, commit }, saleId) {
            const transaction = state.transactions.find(t => t.id === saleId)
            if (transaction) {
                commit('deleteTransaction', transaction)
            }
        },

        async getCashRegisterTransactions({commit, state, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {dateFrom, dateTo, search} = params;
            const options = {
                method: "GET",
                credentials: 'include',
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
            };

            commit("resetSalesPagination");

            const pagination = state.salesPagination;
            const salesParams = new URLSearchParams();
            salesParams.set("date-from", dateFrom);
            salesParams.set("date-to", dateTo);
            salesParams.set("page", 1);
            salesParams.set("page_size", pagination.pageSize);
            if (search) salesParams.set("search", search);

            const urlToGetSales = `${URLS.MODEL.SALE.SALES_URL}?${salesParams.toString()}`;
            const urlToGetTransfers = `${URLS.MODEL.TRANSFER.TRANSFERS_URL}?date-from=${dateFrom}&date-to=${dateTo}`;
            const urlToGetWarehouseMoves = `${URLS.MODEL.WAREHOUSE_MOVE.WAREHOUSE_MOVES_URL}?date-from=${dateFrom}&date-to=${dateTo}`;
            const urlToGetSupplies = `${URLS.MODEL.SUPPLY.SUPPLIES_URL}?date-from=${dateFrom}&date-to=${dateTo}`;

            const salesData = await globalMethods.sendRequestToGetJson({url: urlToGetSales, options: options, successfulHandler: data => data, errorHandler: () => {throw new Error()}});
            const transfersData = await globalMethods.sendRequestToGetJson({url: urlToGetTransfers, options: options, successfulHandler: data => data.data, errorHandler: () => {throw new Error()}});
            const warehouseMovesData = await globalMethods.sendRequestToGetJson({url: urlToGetWarehouseMoves, options: options, successfulHandler: data => data.data, errorHandler: () => {throw new Error()}});
            const suppliesData = await globalMethods.sendRequestToGetJson({url: urlToGetSupplies, options: options, successfulHandler: data => data.data, errorHandler: () => {throw new Error()}});

            const nonSaleTransactions = [...transfersData, ...warehouseMovesData, ...suppliesData];
            nonSaleTransactions.sort((a, b) => new Date(b.date_of_created) - new Date(a.date_of_created));
            commit("setNonSaleTransactions", nonSaleTransactions);

            const sales = salesData.data || salesData;
            const hasNext = salesData.has_next ?? false;
            const totalCount = salesData.total_count ?? (Array.isArray(sales) ? sales.length : 0);

            commit("setSalesPaginated", {
                data: Array.isArray(sales) ? sales : [],
                hasNext,
                totalCount,
                append: false,
            });

            const lastSaleId = Array.isArray(sales) && sales[0] ? sales[0].id : null;
            commit("setLastSaleId", lastSaleId);
            return {successfully: true};
        },
        async getBalanceSummary({rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {dateFrom, dateTo} = params;
            const companyId = company.companyData?.token;
            const shopId = globalMethods.getDataFromLocalStorage("currentCashRegisterId");
            if (!companyId || !shopId) return null;  // not yet loaded — skip silently
            const options = {
                method: "GET",
                credentials: 'include',
                headers: {
                    role: personalData.role,
                    companyId: companyId,
                    shopId: shopId,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
            };
            const url = `${URLS.MODEL.SALE.BALANCE_SUMMARY_URL}?date-from=${dateFrom}&date-to=${dateTo}`;
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler: data => data.data, errorHandler: () => null});
        },
        async getDashboardSummary({rootState}) {
            const {personalData, company, cashRegister} = rootState;
            const companyId = company.companyData?.token;
            const shopId = globalMethods.getDataFromLocalStorage("currentCashRegisterId");
            if (!companyId || !shopId) return null;
            const options = {
                method: "GET",
                credentials: 'include',
                headers: {
                    role: personalData.role,
                    companyId: companyId,
                    shopId: shopId,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
            };
            return globalMethods.sendRequestToGetJson({url: URLS.MODEL.SALE.DASHBOARD_SUMMARY_URL, options, successfulHandler: data => data.data, errorHandler: () => null});
        },
        async loadAllTransactionsForBalance({commit, state, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {dateFrom, dateTo} = params;
            const options = {
                method: "GET",
                credentials: 'include',
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
            };

            let allSales = [];
            let page = 1;
            let hasNext = true;
            const pageSize = 200;

            while (hasNext) {
                const salesParams = new URLSearchParams();
                salesParams.set("date-from", dateFrom);
                salesParams.set("date-to", dateTo);
                salesParams.set("page", page);
                salesParams.set("page_size", pageSize);

                const url = `${URLS.MODEL.SALE.SALES_URL}?${salesParams.toString()}`;
                const salesData = await globalMethods.sendRequestToGetJson({url, options, successfulHandler: data => data, errorHandler: () => { hasNext = false; return null; }});
                if (!salesData) break;

                const sales = salesData.data || salesData;
                if (Array.isArray(sales)) allSales = allSales.concat(sales);
                hasNext = salesData.has_next ?? false;
                page++;
            }

            const merged = [...allSales, ...state.nonSaleTransactions];
            merged.sort((a, b) => new Date(b.date_of_created) - new Date(a.date_of_created));
            commit("setTransactions", merged);
            commit("setSalesPaginationPage", page - 1);
            state.salesPagination.hasNext = false;
            state.salesPagination.totalCount = allSales.length;
            return {successfully: true};
        },
        async loadNextSalesPage({commit, state, rootState}, params) {
            if (!state.salesPagination.hasNext || state.salesPagination.isLoading) return;
            const {personalData, company, cashRegister} = rootState;
            const {dateFrom, dateTo, search} = params;

            commit("setSalesPaginationLoading", true);
            const nextPage = state.salesPagination.page + 1;
            commit("setSalesPaginationPage", nextPage);

            const salesParams = new URLSearchParams();
            salesParams.set("date-from", dateFrom);
            salesParams.set("date-to", dateTo);
            salesParams.set("page", nextPage);
            salesParams.set("page_size", state.salesPagination.pageSize);
            if (search) salesParams.set("search", search);

            const url = `${URLS.MODEL.SALE.SALES_URL}?${salesParams.toString()}`;
            const options = {
                method: "GET",
                credentials: 'include',
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
            };

            const salesData = await globalMethods.sendRequestToGetJson({url, options, successfulHandler: data => data, errorHandler: () => {throw new Error()}});
            const sales = salesData.data || salesData;

            commit("setSalesPaginated", {
                data: Array.isArray(sales) ? sales : [],
                hasNext: salesData.has_next ?? false,
                totalCount: salesData.total_count ?? state.salesPagination.totalCount,
                append: true,
            });
            commit("setSalesPaginationLoading", false);
            return {successfully: true};
        },
        async createSale({commit, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {transactionMethod, client, operations, discount, note, currency} = params;
            const url = URLS.MODEL.SALE.SALES_URL;
            const options = {
                method: "POST",
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    payment: transactionMethod,
                    client: client,
                    discount: Number(discount),
                    operations: operations,
                    note: note,
                    currency: currency,
                }),
            };
            const successfulHandler = (data) => {
                const newTransactionData = data["sale_data"];
                const salaryReportData = data["salary_report_data"];
                commit("addTransaction", newTransactionData);
                commit("cashRegister/increaseTotalAvailableBalance", newTransactionData, {root: true});
                if (!newTransactionData.transfer_to) {
                    commit("good/decreaseCashRegisterGoodsAmount", {newTransactionData: newTransactionData, cashRegisterPk: cashRegister.cashRegisterData.id}, {root: true});
                    commit("client/increaseClientTotalPayment", newTransactionData, {root: true});
                    commit("company/increaseAmountOfTransactions", null, {root: true});
                }
                if (salaryReportData) {
                    commit("salaryReport/addOrUpdateSalaryReport", salaryReportData, {root: true});
                }
                commit("setLastSaleId", newTransactionData["id"]);
                notify(i18n.global.t("Sale successfully added"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async createTransfer({commit, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {payment_method_from, payment_method_to, transfer_to, price, currency, note} = params;
            const url = URLS.MODEL.TRANSFER.TRANSFERS_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    payment_method_from: payment_method_from,
                    payment_method_to: payment_method_to,
                    note: note,
                    currency: currency,
                    transfer_to: transfer_to,
                    total: price,
                    is_transfer: true,
                }),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                const newTransactionData = data["data"];
                commit("addTransaction", newTransactionData);
                commit("cashRegister/increaseTotalAvailableBalance", newTransactionData, {root: true});
                notify(i18n.global.t("Transfer completed successfully"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async createOperation({commit, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {transactionMethod, operations, note, currency} = params;
            const url = URLS.MODEL.SALE.SALES_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    payment: transactionMethod,
                    operations: operations,
                    note: note,
                    currency: currency,
                    discount: 0,
                }),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("addTransaction", data["sale_data"]);
                commit("cashRegister/increaseTotalAvailableBalance", data["sale_data"], {root: true});
                notify(i18n.global.t("Operation successfully added"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async deleteTransaction({commit, rootState}, transactionId) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.SALE.SALE_URL.replace("<ID>", transactionId);
            const options = {
                method: "DELETE",
                headers: {
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                credentials: "include",
            };
            const successfulHandler = (data) => {
                const transactionData = data["sale_data"];
                const salaryReportData = data["salary_report_data"];
                commit("deleteTransaction", transactionData);
                commit("cashRegister/decreaseTotalAvailableBalance", transactionData, {root: true})
                commit("client/decreaseClientTotalPayment", transactionData, {root: true});
                if (salaryReportData) commit("salaryReport/addOrUpdateSalaryReport", salaryReportData, {root: true});
                notify(i18n.global.t("Transaction successfully deleted"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
    },
};
